import { createContext, useContext } from 'react';
import type { User, LoginRequest } from '../lib/api';
import type { TokensDTO } from '../lib/auth/AuthTokensService';

export interface IframeAuthContextType {
  user: User | null;
  login: (credentials: LoginRequest) => Promise<void>;
  logout: () => void;
  isLoading: boolean;
  isAuthenticated: boolean;
  hasRole: (role: string) => boolean;
  hasAnyRole: (roles: string[]) => boolean;
  updateProfile: (data: Partial<User>) => Promise<void>;
  changePassword: (data: { current_password: string; new_password: string }) => Promise<void>;
  // Additional iframe auth properties
  credentials: TokensDTO | null;
  rolesFromToken: string[] | null;
  authTenantId: string;
}

export const IframeAuthContext = createContext<IframeAuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(IframeAuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an IframeAuthProvider');
  }
  return context;
};

// Hook for checking permissions
export const usePermissions = () => {
  const { hasRole, hasAnyRole } = useAuth();

  return {
    // Permission checks
    canManageUsers: hasRole('admin'),
    canManageProjects: hasAnyRole(['admin', 'manager']),
    canManageContent: hasAnyRole(['admin', 'manager']),
    canViewAuditLogs: hasAnyRole(['admin', 'auditor']),
    canManageVendors: hasAnyRole(['admin', 'manager', 'evaluator']),
    canEvaluateResponses: hasAnyRole(['admin', 'manager', 'evaluator']),
    canViewReports: hasAnyRole(['admin', 'manager', 'viewer']),
    canManageSettings: hasRole('admin'),

    // Role flags
    isAdmin: hasRole('admin'),
    isManager: hasRole('manager'),
    isEvaluator: hasRole('evaluator'),
    isAuditor: hasRole('auditor'),
    isViewer: hasRole('viewer'),
  };
};
